import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { TSurveyAnswer } from "@/types"
import { answersAPI } from "@/utils"
import { createResource, updateQueries } from "./utils"

function hasAnswerContent(answer: Partial<TSurveyAnswer>) {
  const hasValue = answer.value !== null && answer.value !== undefined && !Number.isNaN(answer.value)
  const hasComment = answer.comment !== null && answer.comment !== undefined && String(answer.comment).trim().length > 0
  return hasValue || hasComment
}

function normalizeAnswers(resource: Partial<TSurveyAnswer> | Partial<TSurveyAnswer>[]) {
  const list = Array.isArray(resource) ? resource : [resource]
  return list.filter(hasAnswerContent)
}

export function useAPIAnswer(sessionId) {
  const queryClient = useQueryClient()
  const urlKey = answersAPI.url(sessionId)

  const api = useMutation({
    mutationFn: (data: Partial<TSurveyAnswer>[]) => answersAPI.create!(data, sessionId),
    onSuccess: (data) => updateQueries(urlKey, createResource(data)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [urlKey] }),
  })
  return {
    create: (resource: Partial<TSurveyAnswer> | Partial<TSurveyAnswer>[]) => {
      const payload = normalizeAnswers(resource)
      if (!payload.length) return Promise.resolve(undefined)
      return api.mutateAsync(payload)
    },
    isUpdating: api.isPending,
    error: api.error,
    isSuccess: api.isSuccess,
  }
}
